import { audioGraph, duckMusic, setSfxEnabled, sfxEnabled, unlockAudio, type AudioGraph } from "./engine.ts";
import { bell, marimba, noise, pad, pluck, thud } from "./instruments.ts";

/**
 * Game sound cues, all synthesized (WebAudio). Sounds are tuned to one
 * bright major-pentatonic palette so overlapping cues stay consonant.
 */
export type Cue =
  | "tap"
  | "pick"
  | "pop"
  | "error"
  | "undo"
  | "hint"
  | "coin"
  | "coin-tick"
  | "win"
  | "perfect"
  | "bin-done"
  | "phase"
  | "purchase"
  | "achievement"
  | "chapter"
  | "open"
  | "close"
  | "unlock"
  | "whoosh"
  | "record";

export { unlockAudio };

export function setSoundEnabled(value: boolean): void {
  setSfxEnabled(value);
}

/** C major pentatonic from C5 upward: combo notes climb this ladder. */
const LADDER = [72, 74, 76, 79, 81, 84, 86, 88, 91, 93, 96];

function at(graph: AudioGraph, delay = 0): number {
  return graph.ctx.currentTime + 0.005 + delay;
}

/** Correct placement; each placement in a streak climbs one note higher. */
export function playPlace(combo: number): void {
  const graph = ready();
  if (!graph) return;
  const step = Math.max(0, Math.min(LADDER.length - 1, combo - 1));
  const note = LADDER[step] ?? 72;
  const t = at(graph);
  marimba(graph, graph.sfx, note, t, { gain: 0.2 });
  if (combo >= 3) bell(graph, graph.sfx, note + 12, t + 0.01, { gain: 0.05 + Math.min(0.06, combo * 0.006), duration: 0.9 });
  if (combo >= 6) noise(graph, graph.sfx, t, { from: 9000, to: 12000, duration: 0.18, gain: 0.012, type: "highpass", space: 0.5 });
}

/** Star reveal on the result card: 1 → 2 → 3 rise in pitch. */
export function playStar(index: 1 | 2 | 3): void {
  const graph = ready();
  if (!graph) return;
  const t = at(graph);
  const top = [0, 88, 91, 96][index] ?? 88;
  bell(graph, graph.sfx, top, t, { gain: 0.15, duration: 1.4 });
  marimba(graph, graph.sfx, top - 24, t, { gain: 0.12 });
  if (index === 3) sparkleRun(graph, t + 0.08, [96, 100, 103, 108], 0.05);
}

function ready(): AudioGraph | null {
  const graph = audioGraph();
  if (!graph || !sfxEnabled()) return null;
  if (graph.ctx.state === "suspended") void graph.ctx.resume();
  return graph;
}

function sparkleRun(graph: AudioGraph, t: number, notes: readonly number[], gap: number, gain = 0.05): void {
  notes.forEach((note, i) => bell(graph, graph.sfx, note, t + i * gap, { gain, duration: 0.9, pan: (i % 2 ? 0.25 : -0.25) }));
}

function chord(graph: AudioGraph, notes: readonly number[], t: number, length: number, gain = 0.035): void {
  for (const note of notes) pad(graph, graph.sfx, note, t, { gain, duration: length });
}

export function play(cue: Cue): void {
  const graph = ready();
  if (!graph) return;
  const t = at(graph);
  const bus = graph.sfx;
  switch (cue) {
    case "tap":
      pluck(graph, bus, 84, t, { gain: 0.07, duration: 0.18, space: 0.05 });
      break;
    case "pick":
      marimba(graph, bus, 79, t, { gain: 0.09, duration: 0.25 });
      break;
    case "pop":
      playPlace(1);
      break;
    case "error":
      thud(graph, bus, t);
      marimba(graph, bus, 64, t + 0.07, { gain: 0.08, duration: 0.3 });
      break;
    case "undo":
      noise(graph, bus, t, { from: 3200, to: 700, duration: 0.22, gain: 0.04 });
      pluck(graph, bus, 67, t + 0.06, { gain: 0.08, duration: 0.3 });
      break;
    case "hint":
      sparkleRun(graph, t, [84, 88, 91], 0.07, 0.06);
      break;
    case "coin":
      bell(graph, bus, 95, t, { gain: 0.1, duration: 0.5 });
      bell(graph, bus, 100, t + 0.075, { gain: 0.12, duration: 0.9 });
      break;
    case "coin-tick":
      bell(graph, bus, 100 + Math.round(Math.random() * 3), t, { gain: 0.035, duration: 0.25, space: 0.05 });
      break;
    case "bin-done":
      sparkleRun(graph, t, [84, 88, 91, 96], 0.055, 0.07);
      break;
    case "phase":
      noise(graph, bus, t, { from: 500, to: 7000, duration: 0.5, gain: 0.03, space: 0.6 });
      bell(graph, bus, 79, t + 0.12, { gain: 0.1, duration: 1.4 });
      bell(graph, bus, 86, t + 0.2, { gain: 0.08, duration: 1.4 });
      break;
    case "win": {
      duckMusic(2.2);
      [72, 76, 79, 84].forEach((note, i) => marimba(graph, bus, note, t + i * 0.09, { gain: 0.2 }));
      bell(graph, bus, 88, t + 0.36, { gain: 0.12, duration: 1.8 });
      chord(graph, [60, 64, 67, 72], t + 0.3, 1.8);
      sparkleRun(graph, t + 0.5, [96, 100, 103, 108, 112], 0.045, 0.035);
      break;
    }
    case "perfect": {
      duckMusic(2.4);
      [72, 76, 79, 84, 88].forEach((note, i) => marimba(graph, bus, note, t + i * 0.075, { gain: 0.19 }));
      bell(graph, bus, 91, t + 0.38, { gain: 0.13, duration: 2 });
      bell(graph, bus, 96, t + 0.5, { gain: 0.1, duration: 2 });
      chord(graph, [60, 64, 67, 71, 74], t + 0.3, 2.2);
      sparkleRun(graph, t + 0.6, [100, 103, 108, 112, 115], 0.04, 0.03);
      break;
    }
    case "purchase": {
      duckMusic(2);
      noise(graph, bus, t, { from: 1200, to: 9000, duration: 0.35, gain: 0.03, space: 0.5 });
      sparkleRun(graph, t + 0.05, [79, 84, 88, 91, 96, 100], 0.05, 0.08);
      bell(graph, bus, 95, t + 0.38, { gain: 0.1, duration: 0.6 });
      bell(graph, bus, 100, t + 0.45, { gain: 0.12, duration: 1.4 });
      chord(graph, [65, 69, 72, 76], t + 0.35, 1.8, 0.03);
      break;
    }
    case "achievement": {
      duckMusic(2.4);
      [79, 84, 88].forEach((note, i) => bell(graph, bus, note, t + i * 0.12, { gain: 0.12, duration: 1.8 }));
      bell(graph, bus, 91, t + 0.36, { gain: 0.14, duration: 2.2 });
      chord(graph, [55, 64, 67, 72, 76], t + 0.3, 2.4, 0.03);
      sparkleRun(graph, t + 0.55, [103, 108, 112], 0.06, 0.03);
      break;
    }
    case "chapter": {
      duckMusic(3.4);
      const melody: readonly [number, number][] = [
        [72, 0],
        [76, 0.16],
        [79, 0.32],
        [84, 0.48],
        [83, 0.8],
        [84, 0.96],
        [88, 1.12],
      ];
      for (const [note, delay] of melody) marimba(graph, bus, note, t + delay, { gain: 0.18 });
      bell(graph, bus, 91, t + 1.12, { gain: 0.13, duration: 2.6 });
      chord(graph, [53, 60, 65, 69], t, 1.1, 0.03);
      chord(graph, [55, 64, 67, 72, 76], t + 1.1, 2.4, 0.035);
      sparkleRun(graph, t + 1.3, [100, 103, 108, 112, 115, 120], 0.05, 0.03);
      break;
    }
    case "open":
      noise(graph, bus, t, { from: 900, to: 4200, duration: 0.2, gain: 0.025 });
      break;
    case "close":
      noise(graph, bus, t, { from: 4000, to: 900, duration: 0.16, gain: 0.02 });
      break;
    case "unlock":
      sparkleRun(graph, t, [91, 96], 0.08, 0.07);
      break;
    case "whoosh":
      noise(graph, bus, t, { from: 400, to: 3000, duration: 0.25, gain: 0.04 });
      break;
    case "record":
      sparkleRun(graph, t, [88, 91, 96, 100], 0.06, 0.07);
      break;
  }
}
